/**
 * The playbook page's binding of the shared detail-page surface.
 *
 * Everything structural — the shell, the drag, the rail, the persistence —
 * lives in `shared/surface`. This file is the whole of what the playbook
 * adds: its config (`./layout`) and one typed hook over the generic one.
 *
 * It binds the SELECTION-FREE hook: nothing on this page opens the shared
 * detail container. A rule is edited in the full global side panel
 * (`editClauseRule` / `editGlobalRule`), so the surface declares no detail
 * pane and carries no selection typing — see the note in `./layout`.
 */

import {
  useSurfaceWithoutSelection,
  UseDetailSurfaceOptions,
  UseSurfaceWithoutSelectionResult,
} from '../../shared/surface/useDetailSurface';
import { PLAYBOOK_SURFACE, PLAYBOOK_TABS, PlaybookPanelId, PlaybookTab } from './layout';

export { PLAYBOOK_SURFACE, PLAYBOOK_TABS };
export type { PlaybookPanelId, PlaybookTab };

export type PlaybookWorkspace = UseSurfaceWithoutSelectionResult<
  PlaybookPanelId,
  never,
  PlaybookTab
>;

/**
 * The playbook page's layout and tab state.
 *
 * Takes the playbook uuid because the arrangement is remembered per playbook
 * — see the note at the top of `./layout`.
 */
export const usePlaybookWorkspace = (
  playbookId: string,
  options: Omit<UseDetailSurfaceOptions<PlaybookTab>, 'entityId'> = {}
): PlaybookWorkspace =>
  useSurfaceWithoutSelection<PlaybookPanelId, never, PlaybookTab>(
    PLAYBOOK_SURFACE,
    { ...options, entityId: playbookId }
  );
